import type { ComponentType } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { useUnit } from 'effector-react'
import type { UseInfiniteQueryResult } from 'react-query'
import { useNavigate } from 'react-router'
import type { ListOnScrollProps } from 'react-window'
import { type ListChildComponentProps, VariableSizeList } from 'react-window'

import ArrowIcon from '@/assets/icons/icon-arrow.svg'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { $currentCreator } from '@/models/creators.ts'
import { ListScrollDirection } from '@/modules/statistics/social/types'
import { AppRoutes } from '@/utils/constants'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'
import { useResizeObserver } from '@/utils/useResizeObserver'

import type { PaginatedData, PostType, SoundType } from '../../sounds/types'
import { SoundCard } from '../../sounds/ui/sounds-grid/SoundCard.tsx'
import type { ListMediaType } from '../../types'
import { VideoCard } from '../../videos/ui/video-grid/VideoCard.tsx'

interface Props {
  selectedItems: Map<number, boolean>
  onSelectItem: (type: ListMediaType, id: number) => void
  title: string
  listId: string
  mediaType: ListMediaType
  query: UseInfiniteQueryResult<PaginatedData<SoundType | PostType>, unknown>
}

const isSound = (item: SoundType | PostType): item is SoundType => {
  return 'postCount' in item
}

const mediaHeights: Record<ListMediaType, number> = {
  posts: 493,
  sounds: 417,
}

export const ListItemsGroup = ({
  selectedItems,
  onSelectItem,
  title,
  listId,
  mediaType,
  query: { data, isFetchingNextPage, hasNextPage, fetchNextPage },
}: Props) => {
  const listElementRef = useRef<HTMLDivElement>(null)
  const lastVisibleIndexRef = useRef<number | null>(null)
  const scrollOffsetInitialRef = useRef<number>(0)
  const [parentRef, width] = useResizeObserver()

  const [isShow, setIsShow] = useState(true)
  const activeCreator = useUnit($currentCreator)
  const navigate = useNavigate()

  const handleClickViewVideos = useCallback(
    (soundId: number) => {
      if (activeCreator) {
        navigate(
          AppRoutes.TiktokResearch.Lists.Details.TrendingVideos.asUrl(
            activeCreator.id,
            listId,
            String(soundId),
          ),
        )
      }
    },
    [activeCreator, navigate, listId],
  )

  const medias = useMemo(
    () =>
      data?.pages.flatMap((page) => page.content).filter(isTruthyAndNotEmpty),
    [data],
  )
  const hideArrows = medias ? medias?.length <= 4 : true

  const handleToggleSelect = useCallback(
    (id: number) => {
      onSelectItem(mediaType, id)
    },
    [onSelectItem, mediaType],
  )

  const listItemRenderer: ComponentType<
    ListChildComponentProps<(PostType | SoundType)[]>
  > = useCallback(
    ({ data, index, style }) => {
      const media = data[index]

      // Calculate right margin for each item to create spacing between them.
      const marginRight = 16
      const itemStyle = {
        ...style,
        paddingRight: `${marginRight}px`,
      }

      if (lastVisibleIndexRef.current !== undefined) {
        lastVisibleIndexRef.current = index
      }

      return (
        <div style={itemStyle}>
          {isSound(media) ? (
            <SoundCard
              item={media}
              key={media.id}
              toggleSelect={handleToggleSelect}
              isSelected={selectedItems.get(media.id) ?? false}
              onClickViewVideos={() => handleClickViewVideos(media.id)}
            />
          ) : (
            <VideoCard
              item={media}
              key={media.id}
              toggleSelect={handleToggleSelect}
              isSelected={selectedItems.get(media.id) ?? false}
            />
          )}
        </div>
      )
    },
    [selectedItems, handleClickViewVideos, handleToggleSelect],
  )

  const scrollTo = useCallback(
    (scrollOffset: number) => {
      if (listElementRef.current) {
        const offset = (scrollOffsetInitialRef.current =
          scrollOffsetInitialRef.current + scrollOffset)

        listElementRef.current.scrollTo({
          left: offset,
          behavior: 'smooth',
        })
      }
    },
    [scrollOffsetInitialRef],
  )

  const onScroll = useCallback(
    ({
      scrollDirection,
      scrollUpdateWasRequested,
      scrollOffset,
    }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (!scrollUpdateWasRequested) {
        scrollOffsetInitialRef.current = scrollOffset
      }

      const totalMediaCount = medias?.length ?? 0

      if (
        lastItemIndex === null ||
        lastItemIndex !== totalMediaCount - 4 ||
        isFetchingNextPage ||
        !hasNextPage
      ) {
        return
      }

      if (scrollDirection === ListScrollDirection.forward) {
        console.log('fetchNextPage')
        fetchNextPage()
      }
    },
    [isFetchingNextPage, fetchNextPage, hasNextPage, medias],
  )

  const itemSize = useCallback(() => 300, [])

  const handleBackClick = useCallback(() => scrollTo(300), [scrollTo])
  const handleNextClick = useCallback(() => scrollTo(-300), [scrollTo])

  return (
    <div
      className="w-full overflow-hidden"
      ref={parentRef}
    >
      <Separator className="mb-6" />
      <div className="mb-4 flex justify-between">
        <div className="text-xl font-semibold">{title}</div>
        <div className="flex gap-4">
          Show
          <Switch
            checked={isShow}
            onCheckedChange={setIsShow}
            className="bg-secondary-300 [[aria-checked=false]&>*]:bg-[#fff]"
          />
        </div>
      </div>
      {isShow && !!medias?.length && (
        <div className="relative max-w-full overflow-hidden">
          {!hideArrows && (
            <>
              <button
                className="bg-secondary-500 hover:bg-secondary-300 dark:hover:bg-secondary-100 absolute right-0 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-md transition-colors dark:bg-white"
                onClick={handleBackClick}
              >
                <ArrowIcon className="dark:text-secondary-500 h-8 w-8 text-white " />
              </button>
              <button
                className="bg-secondary-500 shadow-neutral-default hover:bg-secondary-300  dark:hover:bg-secondary-100 absolute left-0 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-md transition-colors dark:bg-white"
                onClick={handleNextClick}
              >
                <ArrowIcon className="dark:text-secondary-500 h-8 w-8 rotate-180 text-white" />
              </button>
            </>
          )}

          <VariableSizeList
            className="scrollbar-hide"
            layout="horizontal"
            height={mediaHeights[mediaType]}
            itemSize={itemSize}
            itemCount={medias.length}
            width={width}
            itemData={medias}
            onScroll={onScroll}
            outerElementType="div"
            outerRef={listElementRef}
          >
            {listItemRenderer}
          </VariableSizeList>
        </div>
      )}
    </div>
  )
}
